<template>
  <div
    class="flex items-center gap-2 px-3 py-1.5 bg-[var(--p-surface-50)] dark:bg-[var(--p-surface-900)] border-l-3 border-[var(--p-primary-color)] rounded-sm animate-slide-down"
  >
    <Reply :size="12" class="text-[var(--p-primary-color)] flex-shrink-0" />
    <div class="flex-1 min-w-0">
      <div class="text-[10px] font-semibold text-[var(--p-primary-color)] uppercase tracking-wide">
        {{ replyingToLabel }}
      </div>
      <div
        class="text-[11px] text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)] truncate leading-tight"
      >
        {{ messagePreview }}
      </div>
    </div>
    <button
      @click="$emit('close')"
      class="flex-shrink-0 p-1 rounded-full hover:bg-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-800)] transition-all duration-200 hover:scale-110 active:scale-95"
      :aria-label="t('whatsapp.reply_preview.cancel_reply')"
      :title="t('whatsapp.reply_preview.cancel_reply_escape')"
    >
      <X :size="14" class="text-[var(--p-surface-600)] dark:text-[var(--p-surface-400)]" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { Reply, X } from 'lucide-vue-next'
import type { Message } from '../../types/interfaces'
import { MessageSourceType } from '../../types/enums'
import { useI18n } from 'vue-i18n'

interface Props {
  message: Message
}

const props = defineProps<Props>()
const { t } = useI18n()

const emit = defineEmits<{
  close: []
}>()

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey)
})

const replyingToLabel = computed(() => {
  return props.message.source_type === MessageSourceType.ENTRANTE
    ? t('whatsapp.reply_preview.replying_to_contact')
    : t('whatsapp.reply_preview.replying_to_yourself')
})

const messagePreview = computed(() => {
  const content = props.message.content

  switch (content.type) {
    case 'text':
      return content.content || content.text || ''
    case 'image':
      return `📷 ${t('whatsapp.replied_message.image')}`
    case 'video':
      return `🎥 ${t('whatsapp.replied_message.video')}`
    case 'audio':
      return `🎵 ${t('whatsapp.replied_message.audio')}`
    case 'document':
    case 'file':
      return `📄 ${content.file_name || t('whatsapp.replied_message.document')}`
    case 'location':
      return `📍 ${t('whatsapp.replied_message.location')}`
    case 'sticker':
      return `🎨 ${t('whatsapp.replied_message.sticker')}`
    case 'contact':
      return `👤 ${t('whatsapp.replied_message.contact')}`
    case 'template':
      return `📋 ${t('whatsapp.replied_message.template')}`
    case 'quick_reply':
      return `↩️ ${content.content}`
    case 'interaction':
      return `💬 ${t('whatsapp.replied_message.interaction')}`
    default:
      return t('whatsapp.replied_message.message')
  }
})
</script>

<style scoped>
@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.2s ease-out;
}
</style>
